import ResponsiveRow from '../shared/ResponsiveRow';
import '../../styles/restScreens.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabaseClient } from '../../config/supabase';
import TaskListTable from './TaskListTable';
import BucketGroupedCards from './BucketGroupedCards';
import InlinePanel from './InlinePanel';
import CreateTaskModal from './CreateTaskModal';
import useModuleAccess from '../../hooks/useModuleAccess';

// ============================================================
// TasksView — Module 13 Task Management entry point.
// Direct port of the VWH-Task-GUI dashboard (SharePoint/MSAL) onto
// Supabase, per Module_13_Task_Management_v1_02.md.
//
// Data-loading note: every other module in this app (accounts,
// transactions, invoices...) loads its top-level data in CRMApp.jsx and
// passes it down as props. Tasks owns its own fetch here instead —
// nothing else in the app currently references task data, so there's
// no shared-state reason to route it through CRMApp, and it keeps
// CRMApp.jsx (already large) from growing further. Flagging this as a
// deliberate deviation, not an oversight.
// ============================================================

const BRANDS = ['All', 'General', 'VW', 'VaLyn', 'The Ride', 'smarTEK', 'Po1'];
const STATUSES = ['All', 'Queue', 'In Progress', 'Blocked', 'Completed', 'Cancelled'];
const PRIORITIES = ['All', 'Critical', 'Urgent', 'Important', 'Normal', 'Low'];
const FLAGS = ['All', 'None', 'Blocked', 'On Hold'];

export default function TasksView({ userId, theme = 'light', isUser = false }) {
  const { allowed: editAllowed } = useModuleAccess(isUser ? 'tasks' : null, 'edit');
  const { allowed: manageAllowed } = useModuleAccess(isUser ? 'tasks' : null, 'manage');
  const canEditTasks = !isUser || editAllowed;
  const canManageTasks = !isUser || manageAllowed;
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'card'
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [brandFilter, setBrandFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [flagFilter, setFlagFilter] = useState('All');
  const [showArchived, setShowArchived] = useState(false);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    let query = supabaseClient.from('tasks').select('*').order('due_date', { ascending: true });
    if (!showArchived) query = query.eq('archive_status', 'active');
    const { data, error } = await query;
    if (error) setToast('Error loading tasks: ' + error.message);
    const rows = error ? [] : (data || []);
    if (!error) setTasks(rows);
    setLoading(false);
    return rows;
  }, [showArchived]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  function showToast(message) {
    setToast(message);
    setTimeout(() => setToast(''), 2000);
  }

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      if (brandFilter !== 'All' && t.workspace_tag !== brandFilter) return false;
      if (statusFilter !== 'All' && t.status !== statusFilter) return false;
      if (priorityFilter !== 'All' && t.priority !== priorityFilter) return false;
      if (flagFilter !== 'All' && t.flag !== flagFilter) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const hay = `${t.task_name} ${t.notes || ''} ${t.bucket_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, brandFilter, statusFilter, priorityFilter, flagFilter, searchTerm]);

  async function handlePanelSaved() {
    // Covers both an explicit field Save and a silent subtask/dependency
    // change — either way, refetch and rebind the open panel to the fresh
    // row so it never shows stale values. The panel itself decides when to
    // close (X button); saving just returns it to view mode with live data.
    const rows = await loadTasks();
    setSelectedTask(prev => (prev ? rows.find(r => r.id === prev.id) || null : prev));
  }

  function handlePanelDeleted() {
    loadTasks();
    setSelectedTask(null);
  }

  const selectClass = `text-sm rounded-md border px-2 py-1.5 ${
    theme === 'dark' ? 'bg-gray-800 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-700'
  }`;

  return (
    <div data-m6-layout="tasks" data-m6-component="TasksView" className="h-full flex flex-col">
      {toast && (
        <div className="fixed top-6 right-6 z-[60] bg-gray-900 text-white text-sm px-4 py-2 rounded-md shadow-lg">
          {toast}
        </div>
      )}

      <ResponsiveRow className="flex flex-wrap items-center gap-2 mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search tasks..."
          className={`text-sm rounded-md border px-3 py-1.5 w-56 ${
            theme === 'dark' ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700'
          }`}
        />
        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className={selectClass}>
          {BRANDS.map(b => <option key={b} value={b}>{b === 'All' ? 'All Brands' : b}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass}>
          {STATUSES.map(s => <option key={s} value={s}>{s === 'All' ? 'All Statuses' : s}</option>)}
        </select>
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className={selectClass}>
          {PRIORITIES.map(p => <option key={p} value={p}>{p === 'All' ? 'All Priorities' : p}</option>)}
        </select>
        <select value={flagFilter} onChange={(e) => setFlagFilter(e.target.value)} className={selectClass}>
          {FLAGS.map(f => <option key={f} value={f}>{f === 'All' ? 'All Flags' : f}</option>)}
        </select>
        <label className={`ia-task-archive-toggle flex items-center gap-1.5 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[var(--accent-ink)]" />
          Show archived
        </label>

        <div className="flex-1" />

        <div className={`flex rounded-md border overflow-hidden ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
          <button
            onClick={() => setViewMode('list')}
            className={`text-sm px-3 py-1.5 ${viewMode === 'list' ? 'bg-[var(--btn-fill)] text-[var(--btn-ink)]' : theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'}`}
          >
            List
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`text-sm px-3 py-1.5 ${viewMode === 'card' ? 'bg-[var(--btn-fill)] text-[var(--btn-ink)]' : theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'}`}
          >
            Card
          </button>
        </div>

        {canEditTasks && <button
          onClick={() => setShowCreate(true)}
          className="text-sm px-4 py-1.5 rounded-md bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] font-medium"
        >
          + New Task
        </button>}
      </ResponsiveRow>

      <div className={`flex-1 overflow-auto ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>
        {loading ? (
          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Loading tasks...</div>
        ) : viewMode === 'list' ? (
          <TaskListTable tasks={filteredTasks} theme={theme} onSelectTask={setSelectedTask} />
        ) : (
          <BucketGroupedCards tasks={filteredTasks} theme={theme} onSelectTask={setSelectedTask} />
        )}
      </div>

      {selectedTask && (
        <InlinePanel
          task={selectedTask}
          allTasks={tasks}
          theme={theme}
          onClose={() => setSelectedTask(null)}
          onSaved={handlePanelSaved}
          onDeleted={handlePanelDeleted}
          onShowToast={showToast}
          canEdit={canEditTasks}
          canManage={canManageTasks}
        />
      )}

      {showCreate && (
        <CreateTaskModal
          userId={userId}
          theme={theme}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            showToast('Task created');
            loadTasks();
          }}
        />
      )}
    </div>
  );
}
